<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { MD5 } from "crypto-js";
import store from "@/store";

import { toast } from "@/common/utils/toast";

import {
	memberList,
	memberDel,
	memberRegister,
} from "@/common/api/rest/member";

import type { MemberType } from "@/common/types/member";

import { FormMember } from "@/components";
import {
	baseConfirm,
	baseModal,
	baseTable,
	breadcrumbSimple,
} from "@/common/components";

interface MemberRegisterType {
	name: string;
	login: string;
	email: string;
	birthdate: string;
	password: string;
}

const { t } = useI18n();
const router = useRouter();

const tableData = ref<MemberType[]>([]);
const selected = ref<MemberType | null>(null);
const payload = ref<MemberRegisterType | null>(null);

const limit = ref<number>(5);
const page = ref<number>(0);
const pages = ref<number>(0);
const search = ref<string>("");
const searched = ref<number>(0);
const total = ref<number>(0);

const showModalAdd = ref(false);
const showModalDel = ref(false);
const payloadValid = ref(false);

const tableHeader = computed(() => [
	{ key: "name", label: t("common.labels.name") },
	{ key: "email", label: t("common.labels.email") },
	{ key: "status", label: t("common.labels.status") },
]);

const status = computed(() => ({
	ACTIVE: t("common.labels.active"),
	INACTIVE: t("common.labels.inactive"),
}));

const breadcrumbList = computed(() => [
	{
		key: "members",
		label: t("common.labels.members"),
		url: "/members",
	},
]);

function fetchData() {
	memberList({
		page: page.value,
		limit: limit.value,
		search: search.value,
	})
		.then((response) => {
			const { success, data, message } = response;

			if (success) {
				tableData.value = data.list;

				pages.value = data.infos.pages;
				total.value = data.infos.total;
				searched.value = data.infos.count;
			} else {
				toast({ message: message, type: "error" });
			}
		})
		.catch(() => {
			toast({ message: t("error.default"), type: "error" });
		});
}

function changePage(value: number) {
	page.value = value;
	fetchData();
}

function searchItem(value: string) {
	search.value = value;
	fetchData();
}

function editItem(data: MemberType) {
	router.push(`/members/${data.member_id}/edit`);
}

function removeItem(data: MemberType) {
	selected.value = data;
	showModalDel.value = true;
}

function removeItemCancel() {
	selected.value = null;
	showModalDel.value = false;
}

function removeItemConfirm() {
	if (!selected.value) return;

	store.commit("SET_LOADING", true);

	memberDel(selected.value.member_id)
		.then((response) => {
			const { success, message } = response;

			if (success) {
				toast({ message: message, type: "success" });
				removeItemCancel();
				fetchData();
			} else {
				toast({ message: message, type: "error" });
			}
		})
		.catch(() => {
			toast({ message: t("error.default"), type: "error" });
		})
		.finally(() => {
			store.commit("SET_LOADING", false);
		});
}

function addMember() {
	payload.value = null;
	showModalAdd.value = true;
	payloadValid.value = false;
}

function addMemberCancel() {
	payload.value = null;
	showModalAdd.value = false;
	payloadValid.value = false;
}

function addMemberConfirm() {
	if (!payload.value || !payloadValid.value) return;

	store.commit("SET_LOADING", true);

	memberRegister({
		...payload.value,
		login: payload.value.login.toLowerCase(),
		email: payload.value.email.toLocaleLowerCase(),
		password: MD5(payload.value.password).toString(),
	})
		.then((response) => {
			const { success, message } = response;
			if (success) {
				toast({ message: message, type: "success" });
				addMemberCancel();
				fetchData();
			} else {
				toast({ message: message, type: "error" });
			}
		})
		.catch(() => {
			toast({ message: t("error.default"), type: "error" });
		})
		.finally(() => {
			store.commit("SET_LOADING", false);
		});
}

onMounted(() => {
	fetchData();
});
</script>

<template>
	<article class="member-page">
		<section class="page-header">
			<breadcrumb-simple
				:items="breadcrumbList"
				:show-home="true"
				url-home="/home"
				active="plans"
			/>
		</section>
		<section class="page-content">
			<base-table
				:header="tableHeader"
				:data="tableData"
				:limit="limit"
				:page="page"
				:pages="pages"
				:total="total"
				:searched="searched"
				:options="true"
				:add="true"
				:del="true"
				:edit="true"
				@btn:add="addMember()"
				@change:page="changePage($event)"
				@searching="searchItem($event)"
				@btn:edit="editItem($event)"
				@btn:del="removeItem($event)"
			>
				<template #status="{ data }">
					{{ status[data.status]?.toUpperCase() }}
				</template>

				<template #name="{ data }">
					{{ data.name }}
				</template>
			</base-table>
		</section>
	</article>

	<base-modal
		:show="showModalAdd"
		:title="t('member.modals.titles.member_add').toUpperCase()"
		:show-button-confirm="true"
		:button-confirm="t('common.labels.save')"
		:button-close="t('common.labels.cancel')"
		@close="addMemberCancel()"
		@confirm="addMemberConfirm()"
	>
		<template #content>
			<FormMember
				:data="selected"
				:is-edit="false"
				@update:data="payload = $event"
				@validate="payloadValid = $event"
			/>
		</template>
	</base-modal>

	<base-confirm
		:show="showModalDel"
		:title="t('member.modals.titles.member_remove').toUpperCase()"
		:message="
			t('member.modals.messages.member_remove', {
				name: selected?.first_name,
			})
		"
		@cancel="removeItemCancel()"
		@confirm="removeItemConfirm()"
	/>
</template>

<style lang="scss" scoped>
.member-page {
	display: flex;
	flex-direction: column;
	gap: 1rem;

	.page-header {
		display: flex;
		flex-direction: column;
		gap: 1rem;
		width: inherit;
		background: white;
		border-radius: 0.5rem;
		padding-inline: 1rem;
		padding-block: 0.5rem;
	}

	.page-content {
		display: flex;
		flex-direction: column;
		background: white;
		border-radius: 0.5rem;
		padding-inline: 1.54rem;
		padding-block-start: 1.54rem;
		margin-block-end: 1.54rem;
	}
}
</style>
